import { Injectable } from '@angular/core';
import { Firestore, collection, doc, getDocs, getDoc, updateDoc, query, orderBy, where, limit, startAfter, DocumentData, QueryDocumentSnapshot } from '@angular/fire/firestore';
import { Observable, from, map, switchMap, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AdminOrder } from '../components/admin/order-management/order-management.component';
import { environment } from '../../environments/environment';
import { ShiprocketService } from './shiprocket.service';
import { ToastService } from './toast.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AdminOrderService {
  private readonly ORDERS_COLLECTION = 'orderTable';
  private readonly USERS_COLLECTION = 'users';

  constructor(
    private firestore: Firestore,
    private http: HttpClient,
    private shiprocketService: ShiprocketService,
    private toastService: ToastService,
    private authService: AuthService
  ) {}

  /**
   * Get all orders with user details for admin management
   */
  getAdminOrders(pageSize: number = 20, lastDoc?: QueryDocumentSnapshot<DocumentData>): Observable<{
    orders: AdminOrder[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
    hasMore: boolean;
  }> {
    return from(this.getOrdersQuery(pageSize, lastDoc)).pipe(
      switchMap(querySnapshot => {
        const orders: AdminOrder[] = [];
        const promises: Promise<void>[] = [];

        querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
          const orderData = doc.data();
          const order: AdminOrder = {
            id: doc.id,
            userId: orderData['userId'],
            userName: '',
            userMobile: '',
            products: orderData['products'] || [],
            totalAmount: orderData['totalAmount'] || 0,
            discountAmount: orderData['discountAmount'],
            deliveryCharges: orderData['deliveryCharges'] || 0,
            status: orderData['status'] || 'pending',
            orderDate: orderData['orderDate'],
            shippingDate: orderData['shippingDate'],
            deliveredDate: orderData['deliveredDate'],
            deliveryAddress: orderData['deliveryAddress'] || '',
            paymentMethod: orderData['paymentMethod'] || '',
            paymentStatus: orderData['paymentStatus'] || 'pending',
            doseTypes: orderData['doseTypes'] || [],
            isComboOrder: orderData['isComboOrder'] || false,
            trackingNumber: orderData['trackingNumber'],
            shippingProvider: orderData['shippingProvider'],
            estimatedDelivery: orderData['estimatedDelivery'],
            notes: orderData['notes'],
            shiprocketOrderId: orderData['shiprocketOrderId'],
            shiprocketShipmentId: orderData['shiprocketShipmentId'],
            shiprocketAwbCode: orderData['shiprocketAwbCode'],
            shiprocketCourierName: orderData['shiprocketCourierName'],
            shiprocketManifestUrl: orderData['shiprocketManifestUrl'],
            shiprocketLabelUrl: orderData['shiprocketLabelUrl'],
            shiprocketInvoiceUrl: orderData['shiprocketInvoiceUrl'],
            selectedCourier: orderData['selectedCourier']
          };

          // Fetch user details for this order
          const userPromise = this.getUserDetails(orderData['userId']).then(userDetails => {
            if (userDetails) {
              order.userName = userDetails.name || 'Unknown User';
              order.userMobile = userDetails.mobile || 'No Mobile';
              order.userEmail = userDetails.email || '';
              order.userGender = userDetails.gender || 'other';
              order.userCurrentWeight = userDetails.currentWeight || 0;
              order.userCurrentHeight = userDetails.currentHeight || 0;
              order.userDateOfBirth = userDetails.dateOfBirth?.toDate() || new Date();
              order.userFullAddress = userDetails.fullAddress || '';
              order.userAltMobile = userDetails.altMobile || '';
              order.userRegistrationDate = userDetails.registrationDate?.toDate() || new Date();
              order.userRecentPhoto = userDetails.recentPhoto || '';
              order.userAvatar = userDetails.avatar || '';
              order.userTakenDose = userDetails.takenDose || 0;
              order.userCompletedDoses = userDetails.completedDoses || [];
            }
          });
          promises.push(userPromise);
          orders.push(order);
        });

        return Promise.all(promises).then(() => {
          const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
          const hasMore = querySnapshot.docs.length === pageSize;
          
          return {
            orders,
            lastDoc: lastVisible || null,
            hasMore
          };
        });
      })
    );
  }

  /**
   * Get filtered orders based on search criteria
   */
  getFilteredOrders(
    filters: {
      status?: string;
      paymentStatus?: string;
      searchTerm?: string;
      startDate?: Date;
      endDate?: Date;
    },
    pageSize: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Observable<{
    orders: AdminOrder[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
    hasMore: boolean;
  }> {
    return from(this.getFilteredOrdersQuery(filters, pageSize, lastDoc)).pipe(
      switchMap(querySnapshot => {
        const orders: AdminOrder[] = [];
        const promises: Promise<void>[] = [];

        querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
          const orderData = doc.data();
          const order: AdminOrder = {
            id: doc.id,
            userId: orderData['userId'],
            userName: '',
            userMobile: '',
            products: orderData['products'] || [],
            totalAmount: orderData['totalAmount'] || 0,
            discountAmount: orderData['discountAmount'],
            deliveryCharges: orderData['deliveryCharges'] || 0,
            status: orderData['status'] || 'pending',
            orderDate: orderData['orderDate'],
            shippingDate: orderData['shippingDate'],
            deliveredDate: orderData['deliveredDate'],
            deliveryAddress: orderData['deliveryAddress'] || '',
            paymentMethod: orderData['paymentMethod'] || '',
            paymentStatus: orderData['paymentStatus'] || 'pending',
            doseTypes: orderData['doseTypes'] || [],
            isComboOrder: orderData['isComboOrder'] || false,
            trackingNumber: orderData['trackingNumber'],
            shippingProvider: orderData['shippingProvider'],
            estimatedDelivery: orderData['estimatedDelivery'],
            notes: orderData['notes'],
            shiprocketOrderId: orderData['shiprocketOrderId'],
            shiprocketShipmentId: orderData['shiprocketShipmentId'],
            shiprocketAwbCode: orderData['shiprocketAwbCode'],
            shiprocketCourierName: orderData['shiprocketCourierName'],
            shiprocketManifestUrl: orderData['shiprocketManifestUrl'],
            shiprocketLabelUrl: orderData['shiprocketLabelUrl'],
            shiprocketInvoiceUrl: orderData['shiprocketInvoiceUrl'],
            selectedCourier: orderData['selectedCourier']
          };

          // Fetch user details for this order
          const userPromise = this.getUserDetails(orderData['userId']).then(userDetails => {
            if (userDetails) {
              order.userName = userDetails.name || 'Unknown User';
              order.userMobile = userDetails.mobile || 'No Mobile';
            }
          });
          promises.push(userPromise);
          orders.push(order);
        });

        return Promise.all(promises).then(() => {
          const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
          const hasMore = querySnapshot.docs.length === pageSize;
          
          return {
            orders,
            lastDoc: lastVisible || null,
            hasMore
          };
        });
      })
    );
  }

  /**
   * Get detailed order information including user details and order history
   */
  getOrderDetail(orderId: string): Observable<AdminOrder | null> {
    const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
    
    return from(getDoc(orderRef)).pipe(
      switchMap(orderDoc => {
        if (!orderDoc.exists()) {
          return of(null);
        }

        const orderData = orderDoc.data();
        const order: AdminOrder = {
          id: orderDoc.id,
          userId: orderData['userId'],
          userName: '',
          userMobile: '',
          products: orderData['products'] || [],
          totalAmount: orderData['totalAmount'] || 0,
          discountAmount: orderData['discountAmount'],
          status: orderData['status'] || 'pending',
          orderDate: orderData['orderDate'],
          shippingDate: orderData['shippingDate'],
          deliveredDate: orderData['deliveredDate'],
          deliveryAddress: orderData['deliveryAddress'] || '',
          paymentMethod: orderData['paymentMethod'] || '',
          paymentStatus: orderData['paymentStatus'] || 'pending',
          doseTypes: orderData['doseTypes'] || [],
          isComboOrder: orderData['isComboOrder'] || false,
            trackingNumber: orderData['trackingNumber'],
            shippingProvider: orderData['shippingProvider'],
            estimatedDelivery: orderData['estimatedDelivery'],
            notes: orderData['notes'],
            shiprocketOrderId: orderData['shiprocketOrderId'],
            shiprocketShipmentId: orderData['shiprocketShipmentId'],
            shiprocketAwbCode: orderData['shiprocketAwbCode'],
            shiprocketCourierName: orderData['shiprocketCourierName'],
            shiprocketManifestUrl: orderData['shiprocketManifestUrl'],
            shiprocketLabelUrl: orderData['shiprocketLabelUrl'],
            shiprocketInvoiceUrl: orderData['shiprocketInvoiceUrl']
        };

        // Fetch user details
        return this.getUserDetails(orderData['userId']).then(userDetails => {
          if (userDetails) {
            order.userName = userDetails.name || 'Unknown User';
            order.userMobile = userDetails.mobile || 'No Mobile';
          }
          return order;
        });
      })
    );
  }

  /**
   * Update order status
   * @param skipShipmentValidation - Skip shipment validation (use when shipment was just created)
   */
  updateOrderStatus(orderId: string, status: string, notes?: string, deliveredDateFromShiprocket?: Date, skipShipmentValidation: boolean = false): Promise<void> {
    console.log('=== updateOrderStatus() called ===');
    console.log('OrderId:', orderId);
    console.log('Status:', status);
    console.log('Skip validation:', skipShipmentValidation);
    
    const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
    const updateData: any = { status };

    // Add timestamp for status change
    if (status === 'shipped') {
      updateData['shippingDate'] = new Date();
      // Guard: Do not allow marking as shipped unless Shiprocket shipment exists
      // Skip validation if shipment was just created (to avoid race condition)
      if (!skipShipmentValidation) {
        console.log('Validating shipment exists...');
        return getDoc(orderRef).then(async (orderDoc) => {
          if (!orderDoc.exists()) {
            throw new Error('Order not found');
          }
          const data = orderDoc.data();
          const hasShipment = !!(data['shiprocketShipmentId'] || data['shiprocketAwbCode'] || data['trackingNumber']);
          
          if (!hasShipment) {
            // Retry once after a short delay in case of Firestore eventual consistency
            await new Promise(resolve => setTimeout(resolve, 500));
            const retryDoc = await getDoc(orderRef);
            if (retryDoc.exists()) {
              const retryData = retryDoc.data();
              const retryHasShipment = !!(retryData['shiprocketShipmentId'] || retryData['shiprocketAwbCode'] || retryData['trackingNumber']);
              if (!retryHasShipment) {
                throw new Error('Cannot mark as shipped before creating Shiprocket shipment. Please create the shipment first.');
              }
            } else {
              throw new Error('Cannot mark as shipped before creating Shiprocket shipment. Please create the shipment first.');
            }
          }
          
          const currentHistory = data['orderHistory'] || [];
          updateData['orderHistory'] = [...currentHistory, {
            timestamp: new Date(),
            status,
            notes: notes || '',
            updatedBy: 'admin'
          }];

          return updateDoc(orderRef, updateData);
        });
      } else {
        // Skip validation - shipment was just created, update status directly
        console.log('Skipping validation, updating status directly...');
        return getDoc(orderRef).then(async (orderDoc) => {
          if (!orderDoc.exists()) {
            throw new Error('Order not found');
          }
          const data = orderDoc.data();
          console.log('Current order status in DB:', data['status']);
          const currentHistory = data['orderHistory'] || [];
          updateData['orderHistory'] = [...currentHistory, {
            timestamp: new Date(),
            status,
            notes: notes || '',
            updatedBy: 'admin'
          }];

          console.log('Updating order with data:', updateData);
          await updateDoc(orderRef, updateData);
          console.log('Order status updated successfully in Firestore');
        });
      }
    } else if (status === 'delivered') {
      // Use delivered date from order table (from Shiprocket) if it exists, otherwise use provided date or current date
      return getDoc(orderRef).then(async (orderDoc) => {
        if (!orderDoc.exists()) {
          await updateDoc(orderRef, updateData);
          return;
        }

        const orderData = orderDoc.data();
        
        // Get delivered date from order table (set by Shiprocket tracking) or use provided date
        let deliveredDate: Date;
        if (orderData['deliveredDate']) {
          // Use existing delivered date from order (from Shiprocket)
          deliveredDate = orderData['deliveredDate'].toDate ? orderData['deliveredDate'].toDate() : new Date(orderData['deliveredDate']);
        } else if (deliveredDateFromShiprocket) {
          // Use provided date from Shiprocket tracking
          deliveredDate = deliveredDateFromShiprocket;
        } else {
          // Fallback to current date if no delivered date available
          deliveredDate = new Date();
        }
        
        updateData['deliveredDate'] = deliveredDate;

        const currentHistory = orderData['orderHistory'] || [];
        updateData['orderHistory'] = [...currentHistory, {
          timestamp: new Date(),
          status,
          notes: notes || '',
          updatedBy: 'admin'
        }];

        // Update order first
        await updateDoc(orderRef, updateData);

        // Then create/update dose history for each product in the order
        if (orderData['products'] && Array.isArray(orderData['products'])) {
          const doseUpdates = orderData['products'].map((product: any) => {
            // Start counting from delivery date from order table (from Shiprocket, not purchase date)
            const startDate = new Date(deliveredDate);
            startDate.setHours(0, 0, 0, 0); // Set to midnight for accurate day calculation
            
            // End date is 30 days after delivery
            const endDate = new Date(deliveredDate);
            endDate.setDate(endDate.getDate() + 30); // 30 days dose period from delivery
            endDate.setHours(23, 59, 59, 999); // Set to end of day

            const doseHistory = {
              doseType: product.doseType || product.dose_type,
              doseNumber: product.doseNumber || product.dose_number,
              orderId: orderId,
              startDate, // Delivery date from Shiprocket = when counting starts
              endDate, // Delivery date + 30 days = when counting ends
              status: 'in_progress' as const,
              purchaseDate: orderData['orderDate'] // Keep purchase date for reference
            };

            console.log(`📅 Creating dose history for ${product.doseType}:`, {
              deliveredDate: deliveredDate.toISOString(),
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              daysFromDelivery: 30
            });

            return this.authService.addDoseToHistory(orderData['userId'], doseHistory).toPromise();
          });

          // Wait for all dose updates to complete
          await Promise.all(doseUpdates);
          console.log(`✅ Successfully created dose history for order ${orderId} starting from Shiprocket delivery date: ${deliveredDate.toISOString()}`);
        }
      });
    }

    // Add to order history for non-delivered status changes
    const historyEntry = {
      timestamp: new Date(),
      status,
      notes: notes || '',
      updatedBy: 'admin' // In a real app, this would be the actual admin user ID
    };

    return getDoc(orderRef).then(orderDoc => {
      if (orderDoc.exists()) {
        const currentHistory = orderDoc.data()['orderHistory'] || [];
        updateData['orderHistory'] = [...currentHistory, historyEntry];
      }
      return updateDoc(orderRef, updateData);
    });
  }

  /**
   * Update tracking information
   */
  updateTrackingInfo(orderId: string, trackingData: {
    trackingNumber: string;
    shippingProvider: string;
    estimatedDelivery?: Date;
    courierName?: string;
    courierPhone?: string;
    courierEmail?: string;
  }): Promise<void> {
    const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
    
    return updateDoc(orderRef, {
      trackingNumber: trackingData.trackingNumber,
      shippingProvider: trackingData.shippingProvider,
      estimatedDelivery: trackingData.estimatedDelivery,
      courierName: trackingData.courierName,
      courierPhone: trackingData.courierPhone,
      courierEmail: trackingData.courierEmail,
      lastUpdated: new Date()
    });
  }

  /**
   * Add notes to an order
   */
  addOrderNotes(orderId: string, notes: string, isInternal: boolean = false): Promise<void> {
    const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
    
    return getDoc(orderRef).then(orderDoc => {
      if (orderDoc.exists()) {
        const currentNotes = orderDoc.data()['notes'] || '';
        const newNotes = currentNotes ? `${currentNotes}\n\n${notes}` : notes;
        
        const updateData: any = { notes: newNotes };
        
        if (isInternal) {
          updateData['internalNotes'] = newNotes;
        }
        
        return updateDoc(orderRef, updateData);
      }
      throw new Error('Order not found');
    });
  }

  /**
   * Get order statistics for dashboard
   */
  getOrderStats(): Observable<{
    totalOrders: number;
    pendingOrders: number;
    confirmedOrders: number;
    shippedOrders: number;
    deliveredOrders: number;
    cancelledOrders: number;
    totalRevenue: number;
  }> {
    const ordersRef = collection(this.firestore, this.ORDERS_COLLECTION);
    
    return from(getDocs(ordersRef)).pipe(
      map(querySnapshot => {
        let totalOrders = 0;
        let pendingOrders = 0;
        let confirmedOrders = 0;
        let shippedOrders = 0;
        let deliveredOrders = 0;
        let cancelledOrders = 0;
        let totalRevenue = 0;

        querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
          const data = doc.data();
          totalOrders++;
          
          switch (data['status']) {
            case 'pending':
              pendingOrders++;
              break;
            case 'confirmed':
              confirmedOrders++;
              break;
            case 'shipped':
              shippedOrders++;
              break;
            case 'delivered':
              deliveredOrders++;
              break;
            case 'cancelled':
              cancelledOrders++;
              break;
          }

          if (data['status'] !== 'cancelled') {
            totalRevenue += (data['totalAmount'] || 0) - (data['discountAmount'] || 0);
          }
        });

        return {
          totalOrders,
          pendingOrders,
          confirmedOrders,
          shippedOrders,
          deliveredOrders,
          cancelledOrders,
          totalRevenue
        };
      })
    );
  }

  // Private helper methods
  private async getOrdersQuery(pageSize: number, lastDoc?: QueryDocumentSnapshot<DocumentData>) {
    const ordersRef = collection(this.firestore, this.ORDERS_COLLECTION);
    
    // Get all orders ordered by date (latest first)
    let q = query(ordersRef, orderBy('orderDate', 'desc'), limit(pageSize * 2)); // Get more to allow for sorting
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    
    // Sort the results to prioritize pending orders
    const sortedDocs = snapshot.docs.sort((a, b) => {
      const dataA = a.data();
      const dataB = b.data();
      
      // Prioritize pending orders
      if (dataA['status'] === 'pending' && dataB['status'] !== 'pending') return -1;
      if (dataA['status'] !== 'pending' && dataB['status'] === 'pending') return 1;
      
      // If both have same status, maintain date order (already sorted by orderDate desc)
      return 0;
    });
    
    // Limit to pageSize
    const limitedDocs = sortedDocs.slice(0, pageSize);
    
    // Create a new QuerySnapshot with the sorted results
    return {
      docs: limitedDocs,
      empty: limitedDocs.length === 0,
      size: limitedDocs.length,
      metadata: snapshot.metadata,
      forEach: snapshot.forEach.bind(snapshot),
      docChanges: snapshot.docChanges.bind(snapshot)
    } as any;
  }

  private async getFilteredOrdersQuery(
    filters: any,
    pageSize: number,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ) {
    const ordersRef = collection(this.firestore, this.ORDERS_COLLECTION);
    let constraints: any[] = [orderBy('orderDate', 'desc'), limit(pageSize)];
    
    if (filters.status) {
      constraints.unshift(where('status', '==', filters.status));
    }
    
    if (filters.paymentStatus) {
      constraints.unshift(where('paymentStatus', '==', filters.paymentStatus));
    }
    
    if (filters.startDate) {
      constraints.unshift(where('orderDate', '>=', filters.startDate));
    }
    
    if (filters.endDate) {
      constraints.unshift(where('orderDate', '<=', filters.endDate));
    }
    
    let q = query(ordersRef, ...constraints);
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    return getDocs(q);
  }

  private async getUserDetails(userId: string): Promise<any> {
    try {
      const userRef = doc(this.firestore, this.USERS_COLLECTION, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return null;
    }
  }

  /**
   * Extract pincode from address string (Indian format - 6 digits)
   */
  private extractPincode(address: string): string | null {
    if (!address) return null;
    
    // Match 6 digit pincode (Indian format)
    const pincodeRegex = /\b\d{6}\b/;
    const match = address.match(pincodeRegex);
    
    if (match) {
      return match[0];
    }
    
    return null;
  }

  /**
   * Clean address by removing mobile numbers and extra formatting
   */
  private cleanAddress(address: string): string {
    if (!address) return '';
    
    // Remove mobile number patterns (e.g., "Mobile: 8999716867" or "Mobile:8999716867")
    let cleaned = address.replace(/\s*Mobile\s*:\s*\d{10}/gi, '');
    
    // Remove any remaining mobile number patterns (10 digit numbers at the end)
    cleaned = cleaned.replace(/\s*\d{10}\s*$/g, '');
    
    // Clean up extra spaces and commas
    cleaned = cleaned.replace(/\s+/g, ' ').replace(/,\s*,/g, ',').trim();
    
    // Remove trailing commas and periods
    cleaned = cleaned.replace(/[,\.]+$/, '').trim();
    
    return cleaned;
  }

  /**
   * Extract street address from full address (everything before city)
   */
  private extractStreetAddress(address: string, city: string): string {
    if (!address || !city) return address || '';
    
    const cleaned = this.cleanAddress(address);
    const cityIndex = cleaned.toLowerCase().indexOf(city.toLowerCase());
    
    if (cityIndex > 0) {
      return cleaned.substring(0, cityIndex).replace(/,\s*$/, '').trim();
    }
    
    // If city not found, try to extract first part before last comma
    const parts = cleaned.split(',').map(p => p.trim());
    if (parts.length > 2) {
      return parts.slice(0, -2).join(', ');
    }
    
    return cleaned;
  }

  /**
   * Parse address into components for Shiprocket
   */
  private parseAddress(address: string): { 
    city: string; 
    state: string; 
    pincode: string;
    streetAddress: string;
    fullAddress: string;
  } {
    const pincode = this.extractPincode(address) || environment.shiprocket.warehouse.pincode || '';
    const cleanedAddress = this.cleanAddress(address);
    
    // Parse address components
    // Format is typically: "Name, Street, City, State - Pincode"
    const parts = cleanedAddress.split(',').map(p => p.trim()).filter(p => p);
    
    let city = '';
    let state = '';
    let streetAddress = '';
    
    if (parts.length >= 3) {
      // Last part should be "State - Pincode" or just "State"
      const lastPart = parts[parts.length - 1];
      // Remove pincode from state if present
      state = lastPart.replace(/\s*-\s*\d{6}\s*$/, '').trim();
      
      // Second to last should be city
      city = parts[parts.length - 2] || '';
      
      // Everything before city is street address
      if (parts.length > 2) {
        streetAddress = parts.slice(0, -2).join(', ');
      } else {
        streetAddress = parts[0] || '';
      }
    } else if (parts.length === 2) {
      // Only two parts: assume "City, State"
      city = parts[0] || '';
      state = parts[1].replace(/\s*-\s*\d{6}\s*$/, '').trim();
      streetAddress = '';
    } else if (parts.length === 1) {
      // Single part: try to extract city and state
      const singlePart = parts[0];
      const stateMatch = singlePart.match(/([^-\d]+?)(?:\s*-\s*\d{6})?$/);
      if (stateMatch) {
        state = stateMatch[1].trim();
      }
      city = singlePart.replace(/\s*-\s*\d{6}\s*$/, '').trim();
      streetAddress = '';
    }
    
    // Clean state - remove any remaining pincode or mobile references
    state = state.replace(/\s*-\s*\d{6}.*$/, '').replace(/\s*Mobile\s*:.*$/i, '').trim();
    
    return {
      city: city || 'Mumbai',
      state: state || 'Maharashtra',
      pincode,
      streetAddress: streetAddress || city || 'Address not specified',
      fullAddress: cleanedAddress
    };
  }

  /**
   * Get available courier options for an order
   */
  async getCourierOptions(order: AdminOrder): Promise<{
    success: boolean;
    couriers?: any[];
    error?: string;
  }> {
    try {
      // Extract pincode from delivery address
      const addressParts = this.parseAddress(order.deliveryAddress || order.userFullAddress || '');
      const deliveryPincode = addressParts.pincode;

      if (!deliveryPincode || deliveryPincode.length !== 6) {
        return {
          success: false,
          error: 'Valid delivery pincode is required. Please update the delivery address with a valid pincode.'
        };
      }

      // Calculate package weight (default 0.5 kg if not available)
      const packageWeight = 0.5; // Can be enhanced to calculate from products
      const codFlag: 0 | 1 = order.paymentMethod === 'cod' ? 1 : 0;

      // Check serviceability
      const serviceability = await this.shiprocketService.checkServiceability(
        deliveryPincode,
        packageWeight,
        codFlag
      ).toPromise();

      if (!serviceability?.data?.available_courier_companies || serviceability.data.available_courier_companies.length === 0) {
        return {
          success: false,
          error: 'No courier service available for this pincode. Please contact support.'
        };
      }

      // Sort couriers by rate (cheapest first)
      const couriers = [...serviceability.data.available_courier_companies];
      couriers.sort((a, b) => (a.rate || 0) - (b.rate || 0));

      return {
        success: true,
        couriers: couriers
      };
    } catch (error: any) {
      console.error('Error getting courier options:', error);
      return {
        success: false,
        error: error?.message || 'Failed to get courier options. Please try again.'
      };
    }
  }

  /**
   * Create Shiprocket shipment for an order
   */
  async createShiprocketShipment(
    orderId: string, 
    order: AdminOrder, 
    courierCompanyId?: number
  ): Promise<{
    success: boolean;
    shipmentId?: number;
    awbCode?: string;
    courierName?: string;
    error?: string;
  }> {
    console.log('=== createShiprocketShipment() called ===');
    console.log('OrderId:', orderId);
    console.log('Order exists:', !!order);
    console.log('CourierCompanyId provided:', courierCompanyId);
    
    try {
      // Check if valid shipment already exists (requires BOTH shipmentId AND awbCode)
      // If only shipmentId exists (no awbCode), it means shipment was cancelled/deleted in Shiprocket
      const hasValidShipment = !!(order.shiprocketShipmentId && order.shiprocketAwbCode);
      
      if (hasValidShipment) {
        return {
          success: false,
          error: 'Shipment already created for this order'
        };
      }
      
      // If shipmentId exists but no awbCode, it's an invalid/cancelled shipment
      // Allow recreation by continuing (the invalid data should be cleared before calling this method)
      if (order.shiprocketShipmentId && !order.shiprocketAwbCode) {
        console.warn('Invalid/cancelled shipment detected. Proceeding with new shipment creation.');
      }

      // Extract and parse address components
      const fullAddress = order.deliveryAddress || order.userFullAddress || '';
      const addressParts = this.parseAddress(fullAddress);
      const deliveryPincode = addressParts.pincode;

      if (!deliveryPincode || deliveryPincode.length !== 6) {
        return {
          success: false,
          error: 'Valid delivery pincode is required. Please update the delivery address with a valid pincode.'
        };
      }

      // Calculate package weight (default 0.5 kg if not available)
      const packageWeight = 0.5; // Can be enhanced to calculate from products
      const codFlag: 0 | 1 = order.paymentMethod === 'cod' ? 1 : 0;

      let selectedCourier: any = null;

      // Check if order already has a selected courier (from checkout)
      const orderData = await getDoc(doc(this.firestore, this.ORDERS_COLLECTION, orderId));
      const storedCourier = orderData.exists() ? orderData.data()['selectedCourier'] : null;

      // If courier is not provided, check if it's stored in the order or fetch from serviceability
      if (!courierCompanyId) {
        // First priority: use courier stored during checkout
        if (storedCourier && storedCourier.courierId) {
          courierCompanyId = storedCourier.courierId;
          selectedCourier = storedCourier;
          console.log('Using courier selected during checkout:', storedCourier.courierName);
        } else {
          // Fallback: check serviceability and select courier
          const serviceability = await this.shiprocketService.checkServiceability(
            deliveryPincode,
            packageWeight,
            codFlag
          ).toPromise();

          if (!serviceability?.data?.available_courier_companies || serviceability.data.available_courier_companies.length === 0) {
            return {
              success: false,
              error: 'No courier service available for this pincode. Please contact support.'
            };
          }

          // Select the cheapest courier
          selectedCourier = serviceability.data.available_courier_companies.reduce((prev: any, curr: any) => 
            prev.rate < curr.rate ? prev : curr
          );
          
          courierCompanyId = selectedCourier.courier_company_id;
          console.log('Using cheapest courier from serviceability check:', selectedCourier.courier_name);
        }
      }

      // Get warehouse details
      const warehouse = environment.shiprocket.warehouse;

      // Prepare shipment data
      const nameParts = (order.userName || '').split(' ');
      const firstName = nameParts[0] || 'Customer';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Build proper billing address (street + city)
      const billingStreetAddress = addressParts.streetAddress || addressParts.city || warehouse.city || '';
      const billingAddress = billingStreetAddress ? `${billingStreetAddress}, ${addressParts.city || warehouse.city || ''}` : (addressParts.city || warehouse.city || '');

      // Build proper shipping address (clean, without mobile numbers)
      const shippingStreetAddress = addressParts.streetAddress || '';
      const shippingAddress = shippingStreetAddress 
        ? `${shippingStreetAddress}, ${addressParts.city || warehouse.city || ''}`
        : addressParts.fullAddress || fullAddress;

      // Generate unique order ID for Shiprocket to avoid conflicts with cancelled orders
      // Append timestamp suffix if order might have been cancelled before
      const shiprocketOrderId = order.shiprocketOrderId && !order.shiprocketAwbCode
        ? `${order.id}-${Date.now()}` // Add timestamp if previous shipment was cancelled
        : order.id; // Use regular order ID for first attempt
      
      const shipmentData: any = {
        order_id: shiprocketOrderId,
        order_date: order.orderDate?.seconds ? 
          new Date(order.orderDate.seconds * 1000).toISOString().split('T')[0] : 
          new Date().toISOString().split('T')[0],
        pickup_location: warehouse.name || 'Primary Warehouse',
        billing_customer_name: firstName,
        billing_last_name: lastName,
        billing_address: billingAddress,
        billing_address_2: '',
        billing_city: addressParts.city || warehouse.city || '',
        billing_pincode: deliveryPincode,
        billing_state: addressParts.state || warehouse.state || '',
        billing_country: 'India',
        billing_email: order.userEmail || '',
        billing_phone: order.userMobile || '',
        shipping_is_billing: true,
        shipping_customer_name: firstName,
        shipping_last_name: lastName,
        shipping_address: this.cleanAddress(shippingAddress),
        shipping_address_2: '',
        shipping_city: addressParts.city || warehouse.city || '',
        shipping_pincode: deliveryPincode,
        shipping_state: addressParts.state || warehouse.state || '',
        shipping_country: 'India',
        shipping_email: order.userEmail || '',
        shipping_phone: order.userMobile || '',
        order_items: (order.products || []).map((product: any, index: number) => ({
          name: product.name || `Product ${index + 1}`,
          sku: product.id || `SKU-${index + 1}`,
          units: product.cartQuantity || 1,
          selling_price: product.price ?? 0
        })),
        payment_method: order.paymentMethod === 'cod' ? 'COD' : 'Prepaid',
        // sub_total should be sum of all order_items (units * selling_price)
        sub_total: (order.products || []).reduce((sum: number, product: any) => 
          sum + ((product.price ?? 0) * (product.cartQuantity || 1)), 0
        ),
        weight: packageWeight,
        length: 20, // Default package dimensions in cm (can be made configurable)
        breadth: 15,
        height: 10
      };

      // Add courier_company_id if provided
      if (courierCompanyId) {
        shipmentData.courier_company_id = courierCompanyId;
      }

      // Create shipment via Firebase Function (more secure)
      const functionsUrl = `https://${environment.firebaseFunctions.region}-${environment.firebaseFunctions.projectId}.cloudfunctions.net/createShiprocketShipment`;
      
      console.log('=== Making API call to create shipment ===');
      console.log('URL:', functionsUrl);
      console.log('Pickup location:', shipmentData.pickup_location);
      console.log('Courier company ID:', courierCompanyId);
      console.log('Shipment data keys:', Object.keys(shipmentData));
      
      const response = await this.http.post<any>(functionsUrl, shipmentData).toPromise();
      
      console.log('=== API Response received ===');
      console.log('Response success:', response?.success);
      console.log('Response shipment:', response?.shipment);

      // Validate response - check if shipment was actually created with required fields
      if (response?.success && response.shipment) {
        // Check if shipment status is CANCELED
        const isCancelled = response.shipment.status?.toUpperCase() === 'CANCELED' || 
                           response.shipment.status?.toUpperCase() === 'CANCELLED';
        
        if (isCancelled) {
          console.error('Shiprocket returned a CANCELED shipment:', response);
          return {
            success: false,
            error: 'This order already exists in Shiprocket with CANCELED status. Shiprocket does not allow creating a new shipment with the same order ID.\n\n' +
                   'To fix this, you have 2 options:\n' +
                   '1. Delete the cancelled order from Shiprocket dashboard (Orders → find order ' + response.shipment.order_id + ' → Delete)\n' +
                   '2. Wait for our system to automatically generate a unique order ID on next attempt\n\n' +
                   'After deleting from Shiprocket, refresh this page and try again.'
          };
        }
        
        // Check if shipment has at least one required identifier
        const hasShipmentId = !!(response.shipment.shipment_id || response.shipment.order_id);
        
        if (!hasShipmentId) {
          // Shipment object is empty or invalid
          console.error('Shiprocket returned empty shipment:', response);
          return {
            success: false,
            error: 'Shiprocket returned an empty shipment. This usually means the shipment creation failed. Please check the pickup_location name matches your Shiprocket warehouse exactly, and verify all required fields are provided.'
          };
        }
        
        // Note: AWB code may be null initially - it's assigned after courier selection
        // This is normal Shiprocket behavior, not an error
        const hasAwbCode = !!(response.shipment.awb_code && response.shipment.awb_code.trim());
        
        if (!hasAwbCode) {
          console.warn('Shiprocket order created successfully but AWB code not assigned yet. This is normal - AWB will be assigned after courier selection.');
        }
        
        // Update order with Shiprocket details (only if we have AWB code)
        const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
        
        // Build update object, only including fields that have values
        const updateData: any = {
          shippingProvider: 'shiprocket',
          lastUpdated: new Date()
        };
        
        // Only add fields if they have values (Firestore doesn't allow undefined)
        if (response.shipment.order_id !== undefined && response.shipment.order_id !== null) {
          updateData.shiprocketOrderId = response.shipment.order_id.toString();
        }
        if (response.shipment.shipment_id !== undefined && response.shipment.shipment_id !== null) {
          updateData.shiprocketShipmentId = response.shipment.shipment_id.toString();
        }
        // AWB code and courier name may be null initially - update only if provided
        if (response.shipment.awb_code && response.shipment.awb_code.trim()) {
          updateData.shiprocketAwbCode = response.shipment.awb_code;
          updateData.trackingNumber = response.shipment.awb_code;
        }
        if (response.shipment.courier_name && response.shipment.courier_name.trim()) {
          updateData.shiprocketCourierName = response.shipment.courier_name;
        }
        if (response.shipment.courier_company_id) {
          updateData.shiprocketCourierId = response.shipment.courier_company_id;
        }
        
        await updateDoc(orderRef, updateData);

        return {
          success: true,
          shipmentId: response.shipment.shipment_id,
          awbCode: response.shipment.awb_code,
          courierName: response.shipment.courier_name
        };
      } else {
        throw new Error(response?.error || 'Failed to create shipment');
      }
    } catch (error: any) {
      console.error('Error creating Shiprocket shipment:', error);
      return {
        success: false,
        error: error?.message || 'Failed to create shipment. Please try again.'
      };
    }
  }

  /**
   * Refresh shipment status from Shiprocket to get latest AWB code
   */
  async refreshShipmentStatus(
    orderId: string,
    shipmentId: number
  ): Promise<{
    success: boolean;
    awbCode?: string;
    courierName?: string;
    status?: string;
    error?: string;
  }> {
    try {
      console.log('=== refreshShipmentStatus() called ===');
      console.log('OrderId:', orderId);
      console.log('ShipmentId:', shipmentId);

      // Get order to find Shiprocket order_id
      const orderData = await getDoc(doc(this.firestore, this.ORDERS_COLLECTION, orderId));
      if (!orderData.exists()) {
        return {
          success: false,
          error: 'Order not found'
        };
      }

      const order = orderData.data();
      const shiprocketOrderId = order['shiprocketOrderId'];
      
      if (!shiprocketOrderId) {
        return {
          success: false,
          error: 'Shiprocket order ID not found. Please create shipment first.'
        };
      }

      // Call Shiprocket API to get shipment details using order_id
      const functionsUrl = `https://${environment.firebaseFunctions.region}-${environment.firebaseFunctions.projectId}.cloudfunctions.net/getShiprocketShipment`;
      
      console.log('Calling getShiprocketShipment with order_id:', shiprocketOrderId);
      const response = await this.http.get<any>(`${functionsUrl}?order_id=${shiprocketOrderId}`).toPromise();
      
      console.log('Shipment details response:', response);
      
      if (response?.success && response.shipment) {
        const shipment = response.shipment;
        const hasAwbCode = !!(shipment.awb_code && shipment.awb_code.trim());
        
        // Update order in Firestore with latest shipment data
        const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
        const updateData: any = {
          lastUpdated: new Date()
        };
        
        if (hasAwbCode) {
          updateData.shiprocketAwbCode = shipment.awb_code;
          updateData.trackingNumber = shipment.awb_code;
          console.log('AWB code found:', shipment.awb_code);
        } else {
          console.log('AWB code not yet assigned by Shiprocket');
        }
        
        if (shipment.courier_name && shipment.courier_name.trim()) {
          updateData.shiprocketCourierName = shipment.courier_name;
        }
        
        if (shipment.courier_company_id) {
          updateData.shiprocketCourierId = shipment.courier_company_id;
        }
        
        if (shipment.status) {
          updateData.shiprocketStatus = shipment.status;
        }
        
        await updateDoc(orderRef, updateData);
        console.log('Order updated with latest shipment data');
        
        return {
          success: true,
          awbCode: shipment.awb_code,
          courierName: shipment.courier_name,
          status: shipment.status
        };
      } else {
        return {
          success: false,
          error: response?.error || 'Failed to fetch shipment details from Shiprocket'
        };
      }
    } catch (error: any) {
      console.error('Error refreshing shipment status:', error);
      return {
        success: false,
        error: error?.message || 'Failed to refresh shipment status. Please try again.'
      };
    }
  }
} 